import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { TypeAnimation } from 'react-type-animation';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../GlobalContext';
import { getDays, replaceLocationWithPhotos } from '../helpers/utils';
import './Chat.scss';

const Chat = () => {
  const { messages, setMessages, days, setDays } = useGlobalContext();
  const [inputText, setInputText] = useState('');
  const [currentResponse, setCurrentResponse] = useState({
    bot: '',
    userChoseSummary: '',
    isReadyToCreateItinerary: '',
  });
  const messagesEndRef = useRef(null);

  const [pendingResponse, setPendingResponse] = useState(false);
  const [creatingItinerary, setCreatingItinerary] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = useCallback((event) => {
    setInputText(event.target.value);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const addMessageWithTypingEffect = useCallback(
    (newMessage, typingDuration = 0) => {
      setMessages((prevMessages) => [...prevMessages, newMessage]);

      if (typingDuration > 0) {
        setTimeout(async () => {
          const updatedText =
            newMessage.role === 'assistant'
              ? await replaceLocationWithPhotos(newMessage.content)
              : newMessage.content;
          setMessages((prevMessages) => {
            const lastIndex = prevMessages.length - 1;
            const lastMsg = prevMessages[lastIndex];
            return [
              ...prevMessages.slice(0, lastIndex),
              { ...lastMsg, typing: false, content: updatedText },
            ];
          });
          scrollToBottom();
        }, typingDuration);
      }
    },
    [setMessages, scrollToBottom],
  );

  const fetchResponse = useCallback(
    async (userChoseSummary, newMessages) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}/openai/collect-trip-information`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userChoseSummary,
              messages: newMessages
                .filter((m) => ['user', 'assistant'].includes(m.role))
                .map((m) => ({ role: m.role, content: m.text || m.content }))
                .slice(-2),
            }),
          },
        );

        if (!response.ok)
          throw new Error('Failed to fetch response from backend');

        const data = await response.json();
        console.log({ data });

        if (!!data.bot) {
          setCurrentResponse(data);
        }

        if (data?.events && data.events.length > 0) {
          const newDays = await getDays(data.events);
          setDays(newDays);
        }

        const botResponse = {
          text: data.bot,
          content: data.bot,
          role: 'assistant',
          typing: true,
        };

        addMessageWithTypingEffect(botResponse, data.bot.length * 10);
      } catch (error) {
        console.error('Error calling backend:', error);
        const errorMessage = {
          content: 'Failed to get response.',
          role: 'error',
        };
        setMessages((prevMessages) => [...prevMessages, errorMessage]);
      }
    },
    [setDays, addMessageWithTypingEffect],
  );

  const createItinerary = async (summary) => {
    try {
      setDays({});
      setCreatingItinerary(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/openai/create-itinerary`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            summary,
          }),
        },
      );
      setCreatingItinerary(false);

      if (!response.ok)
        throw new Error('Failed to fetch response from backend');

      const data = await response.json();
      console.log({ data });

      if (data?.events && data.events.length > 0) {
        const newDays = await getDays(data.events);
        console.log({ days: newDays });
        setDays(newDays);
      }
    } catch (error) {
      console.error('Error calling backend:', error);
      const errorMessage = {
        content: 'Failed to create itinerary.',
        role: 'error',
      };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    }
  };

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!inputText || pendingResponse || creatingItinerary) {
        return;
      }
      const userMessage = { content: inputText, role: 'user' };
      const newMessages = [...messages, userMessage];
      setMessages(newMessages);
      setInputText('');
      setPendingResponse(true);
    },
    [inputText, messages, setMessages],
  );

  useEffect(() => {
    scrollToBottom();
  }, [messages, days]);

  useEffect(() => {
    if (currentResponse.isReadyToCreateItinerary) {
      createItinerary(currentResponse.userChoseSummary);
    }
  }, [currentResponse?.userChoseSummary]);

  useEffect(() => {
    if (pendingResponse) {
      fetchResponse(currentResponse.userChoseSummary, messages).then(() =>
        setPendingResponse(false),
      );
    }
  }, [pendingResponse, currentResponse, messages, fetchResponse]);

  const handleViewItineraryClick = useCallback(() => {
    navigate('/itinerary/listview');
  }, [navigate]);

  const renderMessages = useMemo(() => {
    return messages.map((msg, index) => {
      if (msg.typing) {
        return (
          <li
            key={index}
            className={msg.role === 'user' ? 'user-msg' : 'bot-msg'}
          >
            <TypeAnimation
              cursor
              omitDeletionAnimation
              sequence={[msg.content, 30000]}
              wrapper="div"
              repeat={0}
              speed={{ type: 'keyStrokeDelayInMs', value: 10 }}
            />
          </li>
        );
      }
      return (
        <li
          key={`message-${index}`}
          className={msg.role === 'user' ? 'user-msg' : 'bot-msg'}
          dangerouslySetInnerHTML={{ __html: msg.content }}
        />
      );
    });
  }, [messages]);

  return (
    <div className="chat-wrapper">
      <header className="header">Gen AI Travel Planner</header>
      <div className="message-box">
        <div>
          <ul className="messages-list">
            {renderMessages}
            {Object.keys(days || {}).length > 0 && !creatingItinerary && (
              <li
                key="message-view-itinerary"
                className="bot-msg btn-view-itinerary"
                onClick={handleViewItineraryClick}
                role="presentation"
              >
                CLICK HERE TO VIEW ITINERARY
              </li>
            )}
          </ul>
          <div ref={messagesEndRef} />
        </div>
      </div>
      <div className="input-box">
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Enter a prompt here"
            className="input-text chat-input"
            onChange={handleInputChange}
            value={inputText}
          />
          {(pendingResponse || creatingItinerary) && (
            <img src="/typing.gif" className="typing-icon" alt="" />
          )}
          {!pendingResponse && !creatingItinerary && (
            <button
              type="submit"
              className="send-button"
              aria-label="Send Message"
            >
              <img
                src="/images/send-msg.svg"
                className="send-icon"
                alt="Send"
              />
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Chat;
